import { Badge, Button, Checkbox, Divider, Group, Kbd, Stack, Table, Text, TextInput } from "@mantine/core";
import { useField, useForm } from "@mantine/form";
import { useHotkeys } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useRef } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { voiding } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { FeatureType } from "src/gql/graphql";

const Page = () => {
    const { name, features } = Route.useLoaderData();
    const save = useGraphqlMutation(
        graphql(`
            mutation UpdateCustomerFeatures($features: [FeatureType!]!) {
                support {
                    setFeatures(features: $features) {
                        features
                    }
                }
            }
        `),
    );

    const form = useForm({
        initialValues: features,
        mode: "uncontrolled",
    });

    const router = useRouter();
    const search = useField({ initialValue: "" });
    const searchRef = useRef<HTMLInputElement>(null);

    useHotkeys([["/", () => searchRef.current?.focus()]]);

    async function handleSave() {
        const { close } = await notify.confirm(
            <Stack>
                <Text>
                    Are you sure you want to update the features for <strong>{name}</strong>?
                </Text>

                <Group>
                    {Object.entries(form.getDirty())
                        .filter(([_, isDirty]) => isDirty)
                        .map(([feature]) => (
                            <Badge variant="default" key={feature}>
                                {feature}
                            </Badge>
                        ))}
                </Group>

                <Text size="xs" c="dimmed">
                    This action will update the customer's features in the database and locally. Please ensure that the changes are correct
                    before proceeding.
                </Text>
            </Stack>,
            { color: "primary", label: "Save", title: "Update Customer Features" },
        );

        await save.mutateAsync({
            features: Object.entries(form.getValues())
                .filter(([_, enabled]) => enabled)
                .map(([feature]) => feature) as FeatureType[],
        });

        notify.show.success("Customer features updated successfully.");
        await qk.invalidate("customer", "features");
        await router.invalidate();
        form.resetDirty();

        close();
    }

    return (
        <Content paper>
            <Content.Heading backable>Customer Features</Content.Heading>
            <Content.Action>
                <TextInput
                    leftSection={<IconSearch size={16} />}
                    placeholder="Search features..."
                    rightSection={<Kbd>/</Kbd>}
                    ref={searchRef}
                    {...search.getInputProps()}
                />
            </Content.Action>

            <Table striped>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Feature</Table.Th>
                        <Table.Th>Enabled</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {Object.entries(form.values)
                        .filter(([feature]) => feature.toLowerCase().includes(search.getValue().toLowerCase()))
                        .map(([feature]) => (
                            <Table.Tr key={form.key(feature)}>
                                <Table.Td>
                                    <Badge variant="default">{feature}</Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Checkbox {...form.getInputProps(feature, { type: "checkbox" })} />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                </Table.Tbody>
            </Table>

            <Divider mx="-md" />

            <Group justify="flex-end">
                <Button variant="transparent" onClick={() => form.reset()}>
                    Discard Changes
                </Button>
                <Button onClick={voiding(handleSave)} disabled={!form.isDirty()}>
                    Save Changes
                </Button>
            </Group>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/customer/features")({
    component: Page,
    async loader() {
        const { customer } = await httpPostGraphql(
            graphql(`
                query SupportFeatures {
                    customer {
                        name
                        features
                    }
                }
            `),
        );

        return {
            name: customer.name,
            features: Object.fromEntries(
                Object.values(FeatureType).map((feature) => [feature, customer.features.includes(feature)]),
            ) as Record<string, boolean>,
        };
    },
});
